import { Edge, Node } from 'reactflow';
import useStore from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import { ICanvasNodesResponse } from '@store/services/nodes/types';
import { initCollapsedNodes } from '@utils/canvasHelpers';
import {
  formatEdgesForCanvas,
  formatNodeDTOToCanvasNode,
} from '@utils/helpers';
import { Subject } from 'rxjs';

export const saveEntitiesSubject = new Subject<{
  nodes: Node[];
  edges: Edge[];
  oldNodes: Node[];
}>();

const getPersistedCanvasNodes = () =>
  useStore.getState().nodes.filter(({ id }) => !id.startsWith('local'));

const useSaveEntitiesResponse = () => {
  const setNodes = useStore((state) => state.setNodes);
  const setEdges = useStore((state) => state.setEdges);

  const applyToStore = (
    newCanvasNodes: Node[],
    newCanvasEdges: Edge[],
    oldCanvasNodes: Node[],
  ) => {
    setNodes(newCanvasNodes);
    setEdges(newCanvasEdges);

    saveEntitiesSubject.next({
      nodes: newCanvasNodes,
      edges: newCanvasEdges,
      oldNodes: oldCanvasNodes,
    });

    return { nodes: newCanvasNodes, relations: newCanvasEdges };
  };

  const saveResponseToRedux = ({ nodes, relations }: ICanvasNodesResponse) => {
    const oldCanvasNodes = getPersistedCanvasNodes();
    const selectedId = oldCanvasNodes.find((n) => n.selected)?.id;

    const previousEdges = useStore.getState().edges;
    const selectedEdgeId = previousEdges.find((e) => e.selected)?.id;

    const canvasNodes = nodes.map(formatNodeDTOToCanvasNode);

    const customWithSelection = initCollapsedNodes(canvasNodes).map((node) =>
      selectedId ? { ...node, selected: node.id === selectedId } : node,
    );

    const newCanvasNodes = [
      ...oldCanvasNodes.filter(({ type }) =>
        [NodesTypes.LayerNode, NodesTypes.SubLayerNode].includes(
          type as NodesTypes,
        ),
      ),
      ...customWithSelection,
    ];

    const newCanvasEdges = formatEdgesForCanvas(relations);

    const ret = applyToStore(newCanvasNodes, newCanvasEdges, oldCanvasNodes);

    if (
      selectedEdgeId &&
      newCanvasEdges.some((e: Edge) => e.id === selectedEdgeId)
    ) {
      useStore.getState().selectEdge(selectedEdgeId);
    }

    return ret;
  };

  /** Model Evolution: patch only the saved node; keep insight state (hidden, diff, canvas filters). */
  const saveModelEvolutionResponseToRedux = (
    response: ICanvasNodesResponse,
    updatedNodeId: string,
  ) => {
    const oldCanvasNodes = getPersistedCanvasNodes();
    const updatedDto = response.nodes.find((n) => n.id === updatedNodeId);

    if (!updatedDto) {
      return saveResponseToRedux(response);
    }

    const layerNodes = oldCanvasNodes.filter(({ type }) =>
      [NodesTypes.LayerNode, NodesTypes.SubLayerNode].includes(
        type as NodesTypes,
      ),
    );

    const customNodes = oldCanvasNodes.filter(
      ({ type }) => type === NodesTypes.CustomNode,
    );

    const mergedCustom = customNodes.map((node) => {
      if (node.id !== updatedNodeId) return node;

      const fresh = formatNodeDTOToCanvasNode(updatedDto);

      return {
        ...node,
        position: fresh.position,
        parentNode: fresh.parentNode,
        extent: fresh.extent,
        type: fresh.type,
        hidden: node.hidden,
        data: {
          ...node.data,
          dto: fresh.data.dto,
        },
      };
    });

    const newCanvasNodes = [...layerNodes, ...initCollapsedNodes(mergedCustom)];
    const previousEdges = useStore.getState().edges;
    const selectedEdgeId = previousEdges.find((e) => e.selected)?.id;
    const newCanvasEdges = formatEdgesForCanvas(response.relations);

    const ret = applyToStore(newCanvasNodes, newCanvasEdges, oldCanvasNodes);

    if (
      selectedEdgeId &&
      newCanvasEdges.some((e: Edge) => e.id === selectedEdgeId)
    ) {
      useStore.getState().selectEdge(selectedEdgeId);
    }

    return ret;
  };

  return { saveResponseToRedux, saveModelEvolutionResponseToRedux };
};

export default useSaveEntitiesResponse;
